import * as THREE from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";
import type { MapDef, Vec3 } from "@rietz/shared";
import {
  BROADLEAF_CANOPY_DARK,
  BROADLEAF_CANOPY_LIGHT,
  BROADLEAF_TRUNK_COLOR,
  DOOR_FRAME,
  DOOR_STEP,
  DOOR_WOOD,
  DOOR_WOOD_DARK,
  FLOWERBOX_WOOD,
  FLOWER_COLORS,
  FRUIT_CANOPY_DARK,
  FRUIT_CANOPY_LIGHT,
  FRUIT_TRUNK_COLOR,
  GRASS_BASE_COLOR,
  GRASS_TIP_COLOR,
  PINE_NEEDLE_DARK,
  PINE_NEEDLE_LIGHT,
  PINE_TRUNK_COLOR,
  ROCK_COLOR,
  SHUTTER_COLOR,
  SIGN_POST_METAL,
  WINDOW_FRAME,
  WINDOW_GLASS_DARK,
  WINDOW_GLASS_LIGHT,
  WINDOW_SILL,
  hashString,
  rand01,
  randSym,
} from "./palette.js";
import { SHADOWS_ENABLED } from "./shadows.js";

/**
 * Procedural scatter props: pines, broadleaves, fruit trees, grass, rocks,
 * flowerboxes, and the windows (glazed `window`, unglazed `casement`) and doors
 * that turn an extruded box into a house. `visuals` missing, empty or holding
 * an unknown kind must never break a build — unknown kinds are skipped with one
 * warning per kind. Every kind collapses to one `InstancedMesh` per part
 * geometry, and per-instance variation is hash-derived so the forest is
 * identical on every client.
 */

export type PropKind =
  | "pine"
  | "grass"
  | "rock_small"
  | "flowerbox"
  | "broadleaf"
  | "fruit_tree"
  | "window"
  | "casement"
  | "door";

export interface MapProp {
  kind: PropKind;
  pos: Vec3;
  yaw?: number;
  scale?: number;
}

export interface BuiltProps {
  group: THREE.Group;
  /** One per part geometry actually instanced. */
  drawCalls: number;
  triangles: number;
  /** Props accepted, by kind — handy in the smoke test and the debug HUD. */
  counts: Partial<Record<PropKind, number>>;
  dispose(): void;
}

const KNOWN_KINDS = new Set<string>([
  "pine",
  "grass",
  "rock_small",
  "flowerbox",
  "broadleaf",
  "fruit_tree",
  "window",
  "casement",
  "door",
]);

/** Warn once per unknown kind, not once per prop — a bad map has thousands. */
const warnedKinds = new Set<string>();

/**
 * Reads `map.visuals` defensively: the data may come from a newer schema, so
 * every entry is validated.
 */
export function mapVisuals(map: MapDef): MapProp[] {
  const raw = (map as { visuals?: unknown }).visuals;
  if (!Array.isArray(raw)) return [];

  const out: MapProp[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const e = entry as Partial<MapProp>;
    const kind = e.kind;
    const pos = e.pos;
    if (typeof kind !== "string") continue;
    if (
      typeof pos !== "object" ||
      pos === null ||
      !Number.isFinite(pos.x) ||
      !Number.isFinite(pos.y) ||
      !Number.isFinite(pos.z)
    ) {
      continue;
    }
    if (!KNOWN_KINDS.has(kind)) {
      if (!warnedKinds.has(kind)) {
        warnedKinds.add(kind);
        console.warn(`props: unknown PropKind "${kind}" — skipped`);
      }
      continue;
    }
    const prop: MapProp = { kind: kind as PropKind, pos };
    if (Number.isFinite(e.yaw)) prop.yaw = e.yaw as number;
    if (Number.isFinite(e.scale) && (e.scale as number) > 0) {
      prop.scale = e.scale as number;
    }
    out.push(prop);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Build
// ---------------------------------------------------------------------------

/** Scratch for instance composition — `buildProps` allocates no matrices. */
const tmpMatrix = new THREE.Matrix4();
const tmpPos = new THREE.Vector3();
const tmpQuat = new THREE.Quaternion();
const tmpScale = new THREE.Vector3();
const tmpEuler = new THREE.Euler();
const tmpColor = new THREE.Color();

interface KindSpec {
  /** Part geometries, each becoming one InstancedMesh. */
  geometries: () => THREE.BufferGeometry[];
  /** Per-instance uniform-scale jitter, ±fraction. */
  scaleJitter: number;
  /** Per-instance non-uniform squash on X/Z, ±fraction (rocks). */
  squashJitter: number;
  /** Per-instance tint multiplier spread, ±fraction. */
  tintJitter: number;
  /** Whether an absent `yaw` should be hashed in (trees) or left at 0. */
  randomYaw: boolean;
}

const SPECS: Record<PropKind, KindSpec> = {
  pine: {
    geometries: () => [pineTrunkGeometry(), pineFoliageGeometry()],
    scaleJitter: 0.16,
    squashJitter: 0,
    tintJitter: 0.1,
    randomYaw: true,
  },
  broadleaf: {
    geometries: () => [broadleafTrunkGeometry(), broadleafCanopyGeometry()],
    scaleJitter: 0.18,
    squashJitter: 0,
    tintJitter: 0.12,
    randomYaw: true,
  },
  fruit_tree: {
    geometries: () => [fruitTrunkGeometry(), fruitCanopyGeometry()],
    scaleJitter: 0.22,
    squashJitter: 0,
    tintJitter: 0.12,
    randomYaw: true,
  },
  grass: {
    geometries: () => [grassGeometry()],
    scaleJitter: 0.25,
    squashJitter: 0,
    tintJitter: 0.14,
    randomYaw: true,
  },
  rock_small: {
    geometries: () => [rockGeometry()],
    scaleJitter: 0.28,
    squashJitter: 0.25,
    tintJitter: 0.09,
    randomYaw: true,
  },
  flowerbox: {
    geometries: () => [flowerboxGeometry()],
    scaleJitter: 0.05,
    squashJitter: 0,
    tintJitter: 0.04,
    randomYaw: false,
  },
  // Facade fittings: no scale or squash jitter — the generator sized each
  // window
  // for its wall run — and the yaw is the wall normal, never hashed.
  window: {
    geometries: () => [windowGeometry()],
    scaleJitter: 0,
    squashJitter: 0,
    tintJitter: 0.04,
    randomYaw: false,
  },
  casement: {
    geometries: () => [casementGeometry()],
    scaleJitter: 0,
    squashJitter: 0,
    tintJitter: 0.04,
    randomYaw: false,
  },
  door: {
    geometries: () => [doorGeometry()],
    scaleJitter: 0,
    squashJitter: 0,
    tintJitter: 0.06,
    randomYaw: false,
  },
};

/**
 * The part geometries of one kind, freshly built (caller disposes). For the
 * headless smoke test, which asserts invariants an `InstancedMesh` has baked
 * away.
 */
export function debugPartGeometries(kind: PropKind): THREE.BufferGeometry[] {
  return SPECS[kind].geometries();
}

export function buildProps(visuals: readonly MapProp[]): BuiltProps {
  const group = new THREE.Group();
  group.name = "props";

  const byKind = new Map<PropKind, MapProp[]>();
  const counts: Partial<Record<PropKind, number>> = {};
  for (const prop of visuals) {
    const spec = SPECS[prop.kind] as KindSpec | undefined;
    if (spec === undefined) {
      if (!warnedKinds.has(prop.kind)) {
        warnedKinds.add(prop.kind);
        console.warn(`props: unknown PropKind "${prop.kind}" — skipped`);
      }
      continue;
    }
    let list = byKind.get(prop.kind);
    if (list === undefined) {
      list = [];
      byKind.set(prop.kind, list);
    }
    list.push(prop);
  }

  const meshes: THREE.InstancedMesh[] = [];
  const material = new THREE.MeshLambertMaterial({
    vertexColors: true,
    flatShading: true,
    // Grass blades and pine cones are thin; showing both faces costs nothing
    // at this triangle count and removes the "hollow" look from below.
    side: THREE.DoubleSide,
    // Fogged-out props band at the horizon without this — see mapMesh.ts.
    dithering: true,
  });

  let triangles = 0;

  for (const [kind, props] of byKind) {
    const spec = SPECS[kind];
    const kindSalt = hashString(kind);
    const geometries = spec.geometries();
    counts[kind] = props.length;

    for (let g = 0; g < geometries.length; g++) {
      const geo = geometries[g];
      if (geo === undefined) continue;
      const mesh = new THREE.InstancedMesh(geo, material, props.length);
      mesh.name = `${kind}:${g}`;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
      // Under `?shadows=1` only. An `InstancedMesh` casts and receives per
      // instance for free, and shadowed walls with unshadowed crates against
      // them
      // look more broken than no shadows at all.
      if (SHADOWS_ENABLED) {
        mesh.castShadow = true;
        mesh.receiveShadow = true;
      }

      for (let i = 0; i < props.length; i++) {
        const prop = props[i];
        if (prop === undefined) continue;
        const seed = kindSalt + i * 2654435761;

        const s = (prop.scale ?? 1) * (1 + randSym(seed, 1) * spec.scaleJitter);
        const sx = s * (1 + randSym(seed, 2) * spec.squashJitter);
        const sz = s * (1 + randSym(seed, 3) * spec.squashJitter);
        const yaw =
          prop.yaw ?? (spec.randomYaw ? rand01(seed, 4) * Math.PI * 2 : 0);

        tmpPos.set(prop.pos.x, prop.pos.y, prop.pos.z);
        tmpEuler.set(0, yaw, 0);
        tmpQuat.setFromEuler(tmpEuler);
        tmpScale.set(sx, s, sz);
        tmpMatrix.compose(tmpPos, tmpQuat, tmpScale);
        mesh.setMatrixAt(i, tmpMatrix);

        // Multiplier around 1, so the baked vertex colours stay in charge of
        // the *look* and the instance tint only breaks up repetition.
        const j = spec.tintJitter;
        tmpColor.setRGB(
          1 + randSym(seed, 5) * j,
          1 + randSym(seed, 6) * j,
          1 + randSym(seed, 7) * j,
        );
        mesh.setColorAt(i, tmpColor);
      }

      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();

      const index = geo.getIndex();
      const verts =
        index !== null ? index.count : geo.attributes.position?.count ?? 0;
      triangles += (verts / 3) * props.length;

      group.add(mesh);
      meshes.push(mesh);
    }
  }

  return {
    group,
    drawCalls: meshes.length,
    triangles,
    counts,
    dispose(): void {
      for (const mesh of meshes) {
        group.remove(mesh);
        mesh.geometry.dispose();
        mesh.dispose();
      }
      meshes.length = 0;
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------
// Geometry factories. Every factory returns a geometry whose origin is the
// prop's FOOT (y = 0 at the ground), with baked vertex colours. Two invariants
// (`rendersmoke.ts` asserts both):
//  1. The foot is sunk to y = −0.3 m (a rock instead lifts by +0.26 m with its
//     lower facets underground): on any slope the terrain falls away downhill
//     of the trunk, and a stem starting at y = 0 shows daylight under it.
//  2. A trunk's top is buried INSIDE its canopy by at least 0.3 m: blobs are
//     deformed by ±18 % and instances scaled, so a "just touching" gap turns
//     into leaves floating over a stick.
// ---------------------------------------------------------------------------

/** Paints a whole geometry with a vertical gradient between two colours. */
function paintGradient(
  geo: THREE.BufferGeometry,
  bottomHex: number,
  topHex: number,
  yBottom: number,
  yTop: number,
  seed: number,
): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const bottom = new THREE.Color(bottomHex);
  const top = new THREE.Color(topHex);
  const colors = new Float32Array(pos.count * 3);
  const span = yTop - yBottom;
  for (let i = 0; i < pos.count; i++) {
    const t = span > 0 ? clamp01((pos.getY(i) - yBottom) / span) : 0;
    // A hint of per-vertex noise so flat-shaded faces do not band.
    const n = 1 + randSym(seed, i) * 0.05;
    const o = i * 3;
    colors[o] = (bottom.r + (top.r - bottom.r) * t) * n;
    colors[o + 1] = (bottom.g + (top.g - bottom.g) * t) * n;
    colors[o + 2] = (bottom.b + (top.b - bottom.b) * t) * n;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

/** Paints a whole geometry one flat colour (used before merging parts). */
function paintFlat(
  geo: THREE.BufferGeometry,
  hex: number,
  scale = 1,
): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const c = new THREE.Color(hex);
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const o = i * 3;
    colors[o] = c.r * scale;
    colors[o + 1] = c.g * scale;
    colors[o + 2] = c.b * scale;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

function mergeParts(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  for (const p of parts) p.deleteAttribute("uv");
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (merged === null) throw new Error("props: geometry merge failed");
  return merged;
}

/**
 * Pine trunk: a single tapered stem, y ∈ [−0.3, 1.7]; the bottom foliage tier
 * starts at 1.0.
 */
function pineTrunkGeometry(): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(0.09, 0.14, 2.0, 5, 1);
  geo.translate(0, 0.7, 0);
  return mergeParts([
    paintGradient(geo, 0x2f231a, PINE_TRUNK_COLOR, -0.3, 1.7, 11),
  ]);
}

/**
 * Canopy: three shrinking 5-sided cones — a spruce silhouette is carried by the
 * stacked skirts.
 */
function pineFoliageGeometry(): THREE.BufferGeometry {
  const tiers: [number, number, number][] = [
    // [base radius, height, base y]
    [1.15, 1.7, 1.0],
    [0.88, 1.5, 2.05],
    [0.58, 1.35, 3.0],
  ];
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < tiers.length; i++) {
    const tier = tiers[i];
    if (tier === undefined) continue;
    const [r, h, y] = tier;
    const cone = new THREE.ConeGeometry(r, h, 5, 1);
    cone.translate(0, y + h * 0.5, 0);
    // Higher tiers catch more light: bias the gradient upward per tier.
    parts.push(
      paintGradient(
        cone,
        PINE_NEEDLE_DARK,
        PINE_NEEDLE_LIGHT,
        y - 0.4,
        y + h,
        20 + i,
      ),
    );
  }
  return mergeParts(parts);
}

/**
 * One canopy blob: an icosahedron with its corners pushed as `rockGeometry`
 * does (seed quantised by position so shared edges move together). `salt` keeps
 * two blobs of one canopy from deforming identically.
 */
function leafBlobGeometry(
  radius: number,
  amount: number,
  salt: number,
): THREE.BufferGeometry {
  // Polyhedron geometries come non-indexed, i.e. flat-shaded by construction.
  const geo = new THREE.IcosahedronGeometry(radius, 0);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const key =
      Math.round(pos.getX(i) * 64) * 73856093 +
      Math.round(pos.getY(i) * 64) * 19349663 +
      Math.round(pos.getZ(i) * 64) * 83492791 +
      salt * 2654435761;
    pos.setXYZ(
      i,
      pos.getX(i) * (1 + randSym(key, 1) * amount),
      pos.getY(i) * (1 + randSym(key, 2) * amount * 0.8),
      pos.getZ(i) * (1 + randSym(key, 3) * amount),
    );
  }
  geo.computeVertexNormals();
  return geo;
}

/**
 * Broadleaf trunk: a tapered stem, y ∈ [−0.3, 3.3]. The main blob's lowest
 * vertex can be pushed up to ≈ 3.15 m, so a shorter stem left leaves floating
 * above it.
 */
function broadleafTrunkGeometry(): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(0.16, 0.26, 3.6, 6, 1);
  geo.translate(0, 1.5, 0);
  return mergeParts([
    paintGradient(geo, 0x3d3025, BROADLEAF_TRUNK_COLOR, -0.3, 3.3, 71),
  ]);
}

/**
 * Village linden: three overlapping blobs rather than one sphere, so the crown
 * is lumpy rather than a lollipop. ~7.5 m tall, 5 m across.
 */
function broadleafCanopyGeometry(): THREE.BufferGeometry {
  const blobs: [number, number, number, number][] = [
    // [radius, centre x, centre y, centre z]
    [2.45, 0, 4.9, 0],
    [1.7, -1.05, 6.05, 0.8],
    [1.65, 0.95, 5.45, -0.9],
  ];
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < blobs.length; i++) {
    const blob = blobs[i];
    if (blob === undefined) continue;
    const [r, x, y, z] = blob;
    const geo = leafBlobGeometry(r, 0.22, 72 + i);
    geo.translate(x, y, z);
    // Same trick as the pine tiers: bias the gradient per blob so the upper
    // lobes stay lighter than the shaded mass underneath them.
    parts.push(
      paintGradient(
        geo,
        BROADLEAF_CANOPY_DARK,
        BROADLEAF_CANOPY_LIGHT,
        y - r - 0.5,
        y + r,
        75 + i,
      ),
    );
  }
  return mergeParts(parts);
}

/**
 * Orchard apple trunk: a thin stem, y ∈ [−0.3, 2.1], well inside the crown blob
 * at 2.5.
 */
function fruitTrunkGeometry(): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(0.09, 0.13, 2.4, 5, 1);
  geo.translate(0, 0.9, 0);
  return mergeParts([
    paintGradient(geo, 0x3a2c1f, FRUIT_TRUNK_COLOR, -0.3, 2.1, 81),
  ]);
}

/** A single low, round crown — an apple tree kept pruned for picking. */
function fruitCanopyGeometry(): THREE.BufferGeometry {
  const geo = leafBlobGeometry(1.45, 0.2, 82);
  geo.translate(0, 2.5, 0);
  return mergeParts([
    paintGradient(geo, FRUIT_CANOPY_DARK, FRUIT_CANOPY_LIGHT, 0.95, 3.95, 83),
  ]);
}

/**
 * A tuft of three thin blades leaning different ways, starting 3 cm below the
 * origin.
 */
function grassGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (let i = 0; i < 3; i++) {
    const h = 0.26 + rand01(31, i) * 0.14;
    const blade = new THREE.BoxGeometry(0.045, h, 0.012);
    blade.translate(0, h * 0.5, 0);
    blade.rotateZ(randSym(32, i) * 0.45);
    blade.rotateY((i / 3) * Math.PI * 2 + randSym(33, i) * 0.4);
    blade.translate(randSym(34, i) * 0.06, -0.03, randSym(35, i) * 0.06);
    parts.push(
      paintGradient(blade, GRASS_BASE_COLOR, GRASS_TIP_COLOR, -0.03, h - 0.03, 36 + i),
    );
  }
  return mergeParts(parts);
}

/**
 * Rock: a dodecahedron with vertices deterministically pushed, so one shared
 * geometry reads as a rock rather than a die.
 */
function rockGeometry(): THREE.BufferGeometry {
  // Polyhedron geometries are already non-indexed, i.e. flat-shaded by
  // construction — exactly what a faceted boulder wants.
  const geo = new THREE.DodecahedronGeometry(0.42, 0);
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    // Quantise the seed by position so shared corners move together and the
    // hull stays closed.
    const key =
      Math.round(pos.getX(i) * 64) * 73856093 +
      Math.round(pos.getY(i) * 64) * 19349663 +
      Math.round(pos.getZ(i) * 64) * 83492791;
    pos.setXYZ(
      i,
      pos.getX(i) * (1 + randSym(key, 1) * 0.28),
      pos.getY(i) * (1 + randSym(key, 2) * 0.22),
      pos.getZ(i) * (1 + randSym(key, 3) * 0.28),
    );
  }
  geo.computeVertexNormals();
  // Sink it slightly so it reads as embedded rather than balanced on a point.
  geo.translate(0, 0.26, 0);
  return mergeParts([paintGradient(geo, 0x5d6058, ROCK_COLOR, -0.1, 0.6, 41)]);
}

/** Tirol balcony flowerbox: a larch trough packed with geraniums. */
function flowerboxGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const trough = new THREE.BoxGeometry(1.0, 0.24, 0.3);
  trough.translate(0, 0.12, 0);
  parts.push(paintGradient(trough, 0x4e3925, FLOWERBOX_WOOD, 0, 0.24, 51));

  // Foliage mound, then the blossoms poking out of it.
  const foliage = new THREE.BoxGeometry(0.94, 0.14, 0.26);
  foliage.translate(0, 0.3, 0);
  parts.push(paintFlat(foliage, 0x466b33));

  for (let i = 0; i < 7; i++) {
    const d = new THREE.BoxGeometry(0.11, 0.1, 0.11);
    d.translate(
      -0.4 + (i / 6) * 0.8 + randSym(61, i) * 0.03,
      0.38 + rand01(62, i) * 0.05,
      randSym(63, i) * 0.07,
    );
    const hex =
      FLOWER_COLORS[Math.floor(rand01(64, i) * FLOWER_COLORS.length)] ??
      FLOWER_COLORS[0];
    parts.push(paintFlat(d, hex));
  }

  return mergeParts(parts);
}

// ---------------------------------------------------------------------------
// Facade fittings. Windows and doors hang on a wall: `pos` is ON the outer wall
// face and `yaw` the wall's outward normal. Yaw 0 faces −Z (as `signs.ts`), so
// both are authored with the WALL PLANE AT LOCAL z = 0, protruding toward −Z,
// and x = 0 the opening's centre line. They arrive in bulk (tens of thousands
// of windows), hence one merged geometry each.
// ---------------------------------------------------------------------------

/**
 * A shuttered village window: frame, glass, sill, two shutters folded back. x ∈
 * [−0.45, 0.45], y ∈ [0, 1.2] (sill at 0), protrudes ≤ 0.12 m. ≈ 40 triangles.
 */
function windowGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // Frame: the full opening, flush with the wall face.
  const frame = new THREE.BoxGeometry(0.9, 1.2, 0.06);
  frame.translate(0, 0.6, -0.03);
  parts.push(paintFlat(frame, WINDOW_FRAME));

  // Glass 1.5 cm PROUD of the frame rather than recessed: recessed, the two
  // faces z-fight at 100 m.
  const pane = new THREE.BoxGeometry(0.72, 1.0, 0.04);
  pane.translate(0, 0.6, -0.055);
  parts.push(
    paintGradient(pane, WINDOW_GLASS_DARK, WINDOW_GLASS_LIGHT, 0.1, 1.1, 91),
  );

  // Cast-stone sill, wider than the opening and the deepest part of the unit.
  const sill = new THREE.BoxGeometry(1.02, 0.06, 0.12);
  sill.translate(0, -0.01, -0.06);
  parts.push(paintFlat(sill, WINDOW_SILL));

  // Open shutters as single quads (the material is DoubleSide); PlaneGeometry
  // faces +Z, so each is turned to face out.
  for (const x of [-0.64, 0.64]) {
    const shutter = new THREE.PlaneGeometry(0.34, 1.1);
    shutter.rotateY(Math.PI);
    shutter.translate(x, 0.6, -0.03);
    parts.push(paintFlat(shutter, SHUTTER_COLOR));
  }

  return mergeParts(parts);
}

/**
 * An UNGLAZED window: `windowGeometry` minus the pane, sized to the 1.0 m hole
 * a `GlassPaneDef` fills, so the frame covers the reveal. The glazing is the
 * pane instance in `render/glass.ts`, which breaks.
 */
function casementGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  // A real frame — two jambs and a head around the 1.0 m hole, flush with the
  // wall face — so the opening stays open and the pane behind it shows.
  for (const x of [-0.47, 0.47]) {
    const jamb = new THREE.BoxGeometry(0.06, 1.2, 0.06);
    jamb.translate(x, 0.6, -0.03);
    parts.push(paintFlat(jamb, WINDOW_FRAME));
  }
  const head = new THREE.BoxGeometry(1.0, 0.06, 0.06);
  head.translate(0, 1.17, -0.03);
  parts.push(paintFlat(head, WINDOW_FRAME));

  const sill = new THREE.BoxGeometry(1.12, 0.06, 0.12);
  sill.translate(0, -0.01, -0.06);
  parts.push(paintFlat(sill, WINDOW_SILL));

  for (const x of [-0.69, 0.69]) {
    const shutter = new THREE.PlaneGeometry(0.34, 1.1);
    shutter.rotateY(Math.PI);
    shutter.translate(x, 0.6, -0.03);
    parts.push(paintFlat(shutter, SHUTTER_COLOR));
  }

  return mergeParts(parts);
}

/**
 * A house door: leaf, frame, handle and a stone step. x ∈ [−0.55, 0.55], y ∈
 * [−0.02, 2.1] (threshold at 0), protrudes ≤ 0.3 m (the step).
 */
function doorGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const leaf = new THREE.BoxGeometry(0.9, 2.0, 0.06);
  leaf.translate(0, 1.0, -0.03);
  parts.push(paintGradient(leaf, DOOR_WOOD_DARK, DOOR_WOOD, 0, 2.0, 95));

  // Jambs and head, standing 2 cm proud of the leaf on either side.
  for (const x of [-0.49, 0.49]) {
    const jamb = new THREE.BoxGeometry(0.08, 2.06, 0.08);
    jamb.translate(x, 1.03, -0.04);
    parts.push(paintFlat(jamb, DOOR_FRAME));
  }
  const head = new THREE.BoxGeometry(1.06, 0.08, 0.08);
  head.translate(0, 2.04, -0.04);
  parts.push(paintFlat(head, DOOR_FRAME));

  // Handle: a dozen triangles of metal, and the only thing that tells a player
  // which side a door opens from.
  const handle = new THREE.BoxGeometry(0.04, 0.12, 0.05);
  handle.translate(0.32, 1.0, -0.085);
  parts.push(paintFlat(handle, SIGN_POST_METAL));

  const step = new THREE.BoxGeometry(1.1, 0.06, 0.3);
  step.translate(0, 0.01, -0.15);
  parts.push(paintFlat(step, DOOR_STEP));

  return mergeParts(parts);
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
